'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { useTheme } from '../theme/provider'
import { Breadcrumb, BreadcrumbItem } from './breadcrumb'
import { StoryViewer, StoryCollection } from './story-viewer'
import { Play, Clock } from 'lucide-react'

export interface StoriesPageProps {
  /**
   * Page title
   */
  title: string
  /**
   * Page description
   */
  description?: string
  /**
   * Breadcrumb items for navigation
   */
  breadcrumbItems: BreadcrumbItem[]
  /**
   * Story collections to display
   */
  stories: StoryCollection[]
  /**
   * Locale for localization
   */
  locale?: string
  /**
   * Base URL for breadcrumb schema
   */
  baseUrl?: string
  /**
   * Section titles
   */
  sectionTitles?: {
    latest?: string
    archived?: string
  }
  /**
   * Show archived stories
   */
  showArchived?: boolean
}

/**
 * StoriesPage Template - Real Madrid Style
 *
 * Full page for browsing and viewing stories:
 * - Breadcrumb navigation
 * - Page header with title and description
 * - Grid of story collection cards
 * - Click to open fullscreen story viewer
 * - Story metadata (title, timestamp, item count)
 * - Visual indicator for viewed/unviewed stories
 * - Responsive grid layout
 * - Max width container: 1440px
 *
 * NOTE: This component should be wrapped with PageLayout component to ensure
 * consistent header/footer spacing across all pages.
 *
 * @example
 * ```tsx
 * <PageLayout header={headerProps} footer={footerProps}>
 *   <StoriesPage
 *     title="Stories"
 *     description="Explore the latest from Real Madrid"
 *     stories={storyCollections}
 *     breadcrumbItems={breadcrumbs}
 *     locale="en"
 *   />
 * </PageLayout>
 * ```
 */
export function StoriesPage({
  title,
  description,
  breadcrumbItems,
  stories,
  locale = 'en',
  baseUrl,
  sectionTitles = {
    latest: 'Latest Stories',
    archived: 'Archived Stories',
  },
  showArchived = true,
}: StoriesPageProps) {
  const { theme } = useTheme()
  const [showViewer, setShowViewer] = useState(false)
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0)

  const activeStories = stories.filter((story) => !story.isViewed || showArchived)
  const latestStories = stories.filter((story) => !story.isViewed)
  const archivedStories = stories.filter((story) => story.isViewed)

  const openStory = (index: number) => {
    setSelectedStoryIndex(index)
    setShowViewer(true)
  }

  const formatDuration = (itemCount: number) => {
    const seconds = itemCount * 5 // Assume 5 seconds per item
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    return `${minutes}m`
  }

  return (
    <>
      <div
        className="pb-16 transition-colors duration-300"
        style={{ backgroundColor: theme.colors.background }}
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <Breadcrumb
              items={breadcrumbItems}
              locale={locale}
              baseUrl={baseUrl}
            />
          </div>

          {/* Page Header */}
          <header className="mb-12">
            <h1
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: theme.colors.text }}
            >
              {title}
            </h1>
            {description && (
              <p
                className="text-lg md:text-xl max-w-3xl"
                style={{ color: theme.colors.textSecondary }}
              >
                {description}
              </p>
            )}
          </header>

          {/* Latest Stories Section */}
          {latestStories.length > 0 && (
            <section className="mb-12">
              <h2
                className="text-2xl font-bold mb-6"
                style={{ color: theme.colors.text }}
              >
                {sectionTitles.latest}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {latestStories.map((story, index) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    onClick={() => openStory(index)}
                    formatDuration={formatDuration}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Archived Stories Section */}
          {showArchived && archivedStories.length > 0 && (
            <section>
              <h2
                className="text-2xl font-bold mb-6"
                style={{ color: theme.colors.text }}
              >
                {sectionTitles.archived}
              </h2>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {archivedStories.map((story, index) => (
                  <StoryCard
                    key={story.id}
                    story={story}
                    onClick={() => openStory(latestStories.length + index)}
                    formatDuration={formatDuration}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Empty State */}
          {activeStories.length === 0 && (
            <div
              className="text-center py-16 rounded-xl"
              style={{
                backgroundColor: theme.colors.surface,
                borderColor: theme.colors.border,
              }}
            >
              <div className="mb-4">
                <Play className="w-16 h-16 mx-auto opacity-50" style={{ color: theme.colors.textSecondary }} />
              </div>
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: theme.colors.text }}
              >
                No stories available
              </h3>
              <p style={{ color: theme.colors.textSecondary }}>
                Check back later for new stories
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Story Viewer Modal */}
      {showViewer && (
        <StoryViewer
          stories={activeStories}
          initialStoryIndex={selectedStoryIndex}
          onClose={() => setShowViewer(false)}
        />
      )}
    </>
  )
}

/**
 * StoryCard Component - Individual story collection card
 */
interface StoryCardProps {
  story: StoryCollection
  onClick: () => void
  formatDuration: (itemCount: number) => string
}

function StoryCard({ story, onClick, formatDuration }: StoryCardProps) {
  const { theme } = useTheme()

  return (
    <button
      onClick={onClick}
      className="group relative rounded-2xl overflow-hidden aspect-[3/4] transition-all duration-300 hover:scale-105 hover:shadow-2xl"
    >
      {/* Thumbnail Image */}
      <Image
        src={story.thumbnail}
        alt={story.title}
        fill
        className="object-cover"
        sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 20vw"
      />

      {/* Gradient Ring for Unviewed Stories */}
      {!story.isViewed && (
        <div
          className="absolute inset-0 rounded-2xl p-1"
          style={{
            background: `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary || theme.colors.primary})`,
          }}
        >
          <div
            className="w-full h-full rounded-2xl"
            style={{ backgroundColor: theme.colors.background }}
          />
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60 group-hover:from-black/60 group-hover:to-black/80 transition-all duration-300" />

      {/* Play Icon */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center backdrop-blur-md"
          style={{ backgroundColor: `${theme.colors.primary}cc` }}
        >
          <Play className="w-6 h-6 text-white fill-white" />
        </div>
      </div>

      {/* Story Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3">
        {/* Author Avatar (if available) */}
        {story.author?.avatar && (
          <div className="mb-2">
            <div className="relative w-8 h-8 rounded-full overflow-hidden ring-2 ring-white">
              <Image
                src={story.author.avatar}
                alt={story.author.name}
                fill
                className="object-cover"
                sizes="32px"
              />
            </div>
          </div>
        )}

        {/* Title */}
        <h3 className="text-white font-bold text-sm line-clamp-2 mb-1 drop-shadow-lg">
          {story.title}
        </h3>

        {/* Metadata */}
        <div className="flex items-center gap-2 text-xs text-white/90">
          {story.timestamp && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {story.timestamp}
            </span>
          )}
          <span className="w-1 h-1 rounded-full bg-white/60" />
          <span>{story.items.length} {story.items.length === 1 ? 'item' : 'items'}</span>
          <span className="w-1 h-1 rounded-full bg-white/60" />
          <span>{formatDuration(story.items.length)}</span>
        </div>
      </div>

      {/* Viewed Indicator */}
      {story.isViewed && (
        <div
          className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-bold backdrop-blur-md"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
          }}
        >
          Viewed
        </div>
      )}
    </button>
  )
}
